import { useEffect, useRef } from "react";
import { spectrum } from "@/lib/equalizer";
import { barHue, hueOf } from "@/lib/vizMode";

/* Canvas spectrum bars. `mode` = tint | rainbow | flow. Falls back to a gentle idle wave while audio is playing but the analyser isn't live (e.g. before the first gesture). */
export const Visualizer = ({ bars = 24, playing = false, mode = "tint", ledStyle = "rainbow", color = "#18181b", dim = "rgba(24,24,27,0.18)", gap = 2, radius = 1.5, mirror = false, glow = false, className = "", testid = "visualizer" }) => {
  const ref = useRef(null);
  const levels = useRef(new Array(bars).fill(0));
  const peaks = useRef(new Array(bars).fill(0).map(() => ({ v: 0, t: 0 })));
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    let raf, t0 = performance.now();
    const rainbow = mode !== "tint";
    const draw = (now) => {
      const dpr = window.devicePixelRatio || 1;
      const w = cv.clientWidth, h = cv.clientHeight;
      if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const live = playing ? spectrum(bars) : null;
      const lv = levels.current;
      for (let i = 0; i < bars; i++) {
        let target;
        if (live) target = live[i];
        else if (playing) target = 0.25 + 0.2 * Math.sin((now - t0) / 420 + i * 0.7);
        else target = 0;
        lv[i] += (target - lv[i]) * (target > lv[i] ? 0.5 : 0.12);
      }
      const bass = (lv[0] + lv[1] + lv[2]) / 3;
      if (mode === "led") { drawLed(ctx, w, h, lv, peaks.current, bars, gap, now, bass, mirror || h >= 60, ledStyle, hueOf(color)); raf = requestAnimationFrame(draw); return; }
      const bw = (w - gap * (bars - 1)) / bars;
      for (let i = 0; i < bars; i++) {
        const v = Math.max(0.04, Math.min(1, lv[i]));
        const bh = Math.max(2, v * (mirror ? h / 2 : h));
        const x = i * (bw + gap);
        const hue = rainbow ? barHue(i, bars, mode, now) : 0;
        const fill = rainbow ? `hsl(${hue} 92% ${52 + v * 10}%)` : color;
        const track = rainbow ? `hsla(${hue} 90% 55% / 0.12)` : dim;
        if (rainbow && glow) { ctx.shadowColor = `hsl(${hue} 95% 60%)`; ctx.shadowBlur = mode === "flow" ? 4 + bass * 22 : 8; }
        ctx.fillStyle = fill;
        if (mirror) { rounded(ctx, x, h / 2 - bh, bw, bh * 2, radius); }
        else { rounded(ctx, x, h - bh, bw, bh, radius); }
        ctx.fill();
        ctx.shadowBlur = 0;
        if (!mirror) { ctx.fillStyle = track; rounded(ctx, x, 0, bw, h - bh - 1, radius); ctx.fill(); }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [bars, playing, mode, ledStyle, color, dim, gap, radius, mirror, glow]);
  return <canvas ref={ref} className={className} data-testid={testid} data-mode={mode} aria-hidden="true" />;
};

/* Neon LED analyser: stacked light blocks per band with a hue sweep, glow, peak-hold caps, a glowing baseline and a fading reflection. */
function drawLed(ctx, w, h, lv, peaks, bars, gap, now, bass, reflect, style = "rainbow", tintHue = 210) {
  // Colour of block r (0..rows-1) in bar i: rainbow/tint sweep per bar, classic VU green → yellow → red per row.
  const hueAt = (i, r, rows) => style === "classic" ? Math.max(0, 120 - (r / Math.max(1, rows - 1)) * 120) : style === "tint" ? tintHue : barHue(i, bars, "led", now);
  const baseY = reflect ? h * 0.72 : h;
  const top = reflect ? h * 0.72 : h;
  const seg = h >= 60 ? 6 : 3, segGap = h >= 60 ? 2 : 1;
  const rows = Math.max(3, Math.floor((top - 2) / (seg + segGap)));
  const bw = (w - gap * (bars - 1)) / bars;
  ctx.fillStyle = "rgba(0,0,0,0)";
  if (h >= 60) {
    ctx.strokeStyle = "rgba(255,255,255,0.045)";
    ctx.lineWidth = 1;
    for (let r = 0; r <= rows; r++) { const y = baseY - r * (seg + segGap) + 0.5; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
  }
  for (let i = 0; i < bars; i++) {
    const v = Math.max(0, Math.min(1, lv[i]));
    const lit = Math.round(v * rows);
    const x = i * (bw + gap);
    const pk = peaks[i];
    if (v >= pk.v) { pk.v = v; pk.t = now; }
    else if (now - pk.t > 350) pk.v = Math.max(v, pk.v - 0.012);
    for (let r = 0; r < rows; r++) {
      const y = baseY - (r + 1) * (seg + segGap) + segGap;
      const on = r < lit;
      const hue = hueAt(i, r, rows);
      const light = on ? (style === "tint" ? 50 + (r / rows) * 30 : 58 + (r / rows) * 14) : 18;
      ctx.shadowBlur = on && h >= 60 ? 6 + bass * 10 : 0;
      ctx.shadowColor = `hsl(${hue} 100% 65%)`;
      ctx.fillStyle = on ? `hsl(${hue} 100% ${light}%)` : `hsla(${hue} 80% 50% / 0.10)`;
      rounded(ctx, x, y, bw, seg, 1); ctx.fill();
      if (on && h >= 60) { ctx.shadowBlur = 0; ctx.fillStyle = "rgba(255,255,255,0.35)"; rounded(ctx, x + bw * 0.18, y + 1, bw * 0.64, Math.max(1, seg * 0.25), 1); ctx.fill(); }
    }
    ctx.shadowBlur = 0;
    const capRow = Math.round(pk.v * rows);
    if (capRow > lit && capRow > 0) {
      const y = baseY - capRow * (seg + segGap) + segGap;
      ctx.shadowBlur = h >= 60 ? 10 : 0; ctx.shadowColor = `hsl(${hueAt(i, capRow - 1, rows)} 100% 70%)`;
      ctx.fillStyle = `hsl(${hueAt(i, capRow - 1, rows)} 100% 85%)`;
      rounded(ctx, x, y, bw, Math.max(2, seg * 0.6), 1); ctx.fill();
      ctx.shadowBlur = 0;
    }
  }
  if (!reflect) return;
  const grad = ctx.createLinearGradient(0, 0, w, 0);
  for (let s = 0; s <= 6; s++) grad.addColorStop(s / 6, `hsl(${style === "classic" ? 120 - s * 20 : hueAt(Math.round((s / 6) * (bars - 1)), 0, rows)} 100% 70%)`);
  ctx.shadowBlur = 14 + bass * 16; ctx.shadowColor = "rgba(255,255,255,0.8)";
  ctx.fillStyle = grad;
  ctx.fillRect(0, baseY - 1, w, 2);
  ctx.shadowBlur = 0;
  ctx.save();
  ctx.translate(0, baseY * 2 + 2);
  ctx.scale(1, -1);
  ctx.globalAlpha = 0.28;
  for (let i = 0; i < bars; i++) {
    const v = Math.max(0, Math.min(1, lv[i]));
    const lit = Math.round(v * rows);
    const x = i * (bw + gap);
    for (let r = 0; r < Math.min(lit, Math.floor(rows * 0.4)); r++) {
      const y = baseY - (r + 1) * (seg + segGap) + segGap;
      ctx.fillStyle = `hsla(${hueAt(i, r, rows)} 100% 60% / ${1 - r / (rows * 0.4)})`;
      rounded(ctx, x, y, bw, seg, 1); ctx.fill();
    }
  }
  ctx.restore();
  const fade = ctx.createLinearGradient(0, baseY + 2, 0, h);
  fade.addColorStop(0, "rgba(0,0,0,0)"); fade.addColorStop(1, "rgba(0,0,0,0.9)");
  ctx.fillStyle = fade;
  ctx.fillRect(0, baseY + 2, w, h - baseY - 2);
}

function rounded(ctx, x, y, w, h, r) {
  if (h <= 0 || w <= 0) { ctx.beginPath(); return; }
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
